import {
    BetPickEntryFragment,
    BetPickRaceDayFolderBaseFragment,
    BetPickRaceDayFolderFragment,
    BetPickRaceFolderBaseFragment,
    BetPickRaceFolderFragment,
    KolProfileBaseFragment,
    KolProfileFragment,
} from '@graphqlTypes/sitecore';
import { DataResponse } from '@src/common/data-response';
import { KolBasicData } from '@src/common/kol-basic-data';
import { DeepReadonly } from '@src/utils/type-utils';
import { makeAutoObservable } from 'mobx';
import { computedFn } from 'mobx-utils';

export class KolStore {
    constructor() {
        makeAutoObservable(this, undefined, { deep: false });
    }

    private _data: DeepReadonly<KolBasicData> | null = null;
    public get data(): DeepReadonly<KolBasicData> | null {
        return this._data;
    }
    public set data(v: DeepReadonly<KolBasicData> | null) {
        this._data = v;
    }

    private _isLoading = true;
    public get isLoading(): boolean {
        return this._isLoading;
    }
    public set isLoading(v: boolean) {
        this._isLoading = v;
    }

    private _error: Error | null = null;
    public get error(): Error | null {
        return this._error;
    }
    public set error(v: Error | null) {
        this._error = v;
    }

    public setDataResponse(value: DataResponse<KolBasicData>) {
        this._data = value.data || null;
        this._isLoading = value.isLoading;
        this._error = value.error || null;
    }

    private _locale = '';
    public get locale(): string {
        return this._locale;
    }
    public set locale(v: string) {
        this._locale = v;
    }

    private _showOddsChanges = false;
    public get showOddsChanges(): boolean {
        return this._showOddsChanges;
    }
    public set showOddsChanges(v: boolean) {
        this._showOddsChanges = v;
    }

    getProfile = computedFn(
        (key: string | undefined): DeepReadonly<KolProfileFragment> | undefined => {
            const pf = this.data?.kolData.kolItem?.kolProfiles.find((pf) => {
                if (pf.name === key) {
                    return true;
                }
            });
            return pf;
        }
    );

    getRaceDay = computedFn(
        (key: string | undefined): DeepReadonly<BetPickRaceDayFolderFragment> | undefined => {
            const raceDay = this.data?.kolData.betPickItem?.raceDays.find((raceDay) => {
                if (raceDay.name === key) {
                    return true;
                }
            });
            return raceDay;
        }
    );

    getRace = computedFn(
        (
            raceDayKey: string | undefined,
            raceKey: string | undefined
        ): DeepReadonly<BetPickRaceFolderFragment> | undefined => {
            const race = this.getRaceDay(raceDayKey)?.races.find((race) => race.name === raceKey);
            return race;
        }
    );

    get allBetPicks(): DeepReadonly<BetPickDetail[]> | undefined {
        const betpicks: DeepReadonly<BetPickDetail>[] = [];
        this.data?.kolData.betPickItem?.raceDays.forEach((raceDay) => {
            raceDay.races.forEach((race) => {
                const entries = race.betPickEntries
                    .filter((bp) => {
                        return bp.versions.length > 0;
                    })
                    .map<DeepReadonly<BetPickDetail>>((bp) => {
                        return {
                            betpick: bp,
                            race,
                            raceDay,
                            leader: this.getProfile(bp.kol?.targetItem?.name),
                        };
                    });
                betpicks.push(...entries);
            });
        });
        return betpicks;
    }

    get badges() {
        return this.data?.badgesIconData.Item?.data || [];
    }
}

export interface BetPickDetail {
    betpick: BetPickEntryFragment;
    raceDay: BetPickRaceDayFolderBaseFragment | undefined;
    race: BetPickRaceFolderBaseFragment | undefined;
    leader: KolProfileBaseFragment | undefined;
}
